'use client';

import { useState, useEffect } from 'react';
import { ThemedImage } from '@/components/ui/themed-image';
import { ChatCircle, CheckCircle, Star, Info } from '@phosphor-icons/react';
import { PromotionModal } from './promotion-modal';
import { getProductById, type ProductWithCategories } from '@/lib/api/products';

// ID ролла за отзыв
const REVIEW_ROLL_ID = 80;

interface ReviewModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Условия акции с сервера (site_promotion_terms). Пусто или не передали —
   * показываем зашитый текст: пустой блок условий у публичной оферты хуже,
   * чем устаревший.
   */
  terms?: readonly string[];
}

/** Условия на 16.08.2026 — запасной текст, если сервер не ответил. */
const FALLBACK_TERMS = [
  'Оставьте отзыв на любой из платформ: Яндекс.Карты, 2ГИС или ВК',
  'Минимальная сумма заказа — 1200₽',
  'Напишите в поддержку после оставления отзыва',
  'Отзыв должен быть развёрнутым и содержательным (не менее 50 символов)',
  'Отзыв должен быть честным и отражать реальный опыт',
  'Ролл добавляется к следующему заказу автоматически',
  'Акция действует для каждого отзыва один раз',
  'Акция суммируется с другими акциями',
  'Ролл за отзыв и ролл за первый заказ не суммируются между собой',
  'В сумму чека включена стоимость только выбранных вами блюд. Обратите внимание: оплата доставки, приборов и соусов рассчитывается отдельно и не включается в общую сумму чека.',
];

export function ReviewModal({ open, onClose, terms }: ReviewModalProps) {
  const [reviewRoll, setReviewRoll] = useState<ProductWithCategories | null>(null);

  useEffect(() => {
    if (!open) return;
    getProductById(REVIEW_ROLL_ID, true)
      .then(setReviewRoll)
      .catch(() => setReviewRoll(null));
  }, [open]);

  return (
    <PromotionModal
      open={open}
      onClose={onClose}
      title="Ролл за отзыв"
      icon={<ChatCircle className="w-5 h-5 md:w-6 md:h-6 text-white" weight="fill" />}
    >
      <div className="px-4 pt-5 pb-4 md:px-6 md:pt-6 md:pb-6 space-y-6 md:space-y-8">
        {/* Hero Image */}
        <div className="relative w-full h-48 md:h-64 rounded-card overflow-hidden border-2 border-[var(--border-default)] bg-[var(--surface-tertiary)]">
          <ThemedImage
            src={reviewRoll?.image_url}
            alt={reviewRoll?.name || 'Ролл за отзыв'}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 80vw"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-elevated)]/80 via-transparent to-transparent" />
        </div>

        {/* Description */}
        <div className="p-4 md:p-6 rounded-card bg-gradient-to-br from-[var(--primary-subtle)] to-[var(--primary-muted)] border border-[var(--primary)]/20">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-card bg-[var(--primary)] flex items-center justify-center flex-shrink-0">
              <Star className="w-5 h-5 text-white" weight="fill" />
            </div>
            <div className="flex-1">
              <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)] mb-2">
                Ваше мнение важно для нас!
              </h3>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed">
                Оставьте отзыв о нашем сервисе и получите ролл в подарок! 
                Ваше мнение очень важно для нас, и мы хотим отблагодарить вас за обратную связь.
              </p>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-[var(--primary)]" weight="fill" />
            <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)]">
              Как получить подарок?
            </h3>
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
              <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">1</span>
              </div>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                Получите и оцените ваш заказ
              </p>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
              <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">2</span>
              </div>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                Оставьте развернутый отзыв о качестве блюд и сервисе
              </p>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]">
              <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-bold text-white">3</span>
              </div>
              <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                Получите ролл в подарок к следующему заказу
              </p>
            </div>
          </div>
        </div>

        {/* Conditions */}
        <div>
          <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)] mb-3">
            Условия акции
          </h3>
          <div className="space-y-2">
            {(terms && terms.length > 0 ? terms : FALLBACK_TERMS).map((condition, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-pill bg-[var(--primary-subtle)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3 h-3 text-[var(--primary)]" weight="fill" />
                </div>
                <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                  {condition}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Highlight */}
        <div className="p-4 md:p-6 rounded-card bg-gradient-to-r from-[var(--primary-subtle)] to-[var(--primary-muted)] border border-[var(--primary)]/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-card bg-[var(--primary)] flex items-center justify-center">
              <ChatCircle className="w-6 h-6 text-white" weight="fill" />
            </div>
            <div>
              <p className="text-base md:text-lg text-[var(--text-primary)] font-bold mb-1">
                💬 Оставьте отзыв и получите ролл в подарок!
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                Помогите нам стать лучше, поделившись своим мнением
              </p>
            </div>
          </div>
        </div>
      </div>
    </PromotionModal>
  );
}
